/**
 * `/tina/site-health` — the per-site break list the owner reads daily.
 *
 * Tina's `tina-site-health` Job runs eleven checks at 12:00 Athens and keeps
 * `site_health_findings`; this page reads the open (and, on request, snoozed) findings,
 * groups same-platform same-check sites into one card, and offers snooze, solved, disable,
 * discover test and "run now". Spec: tina `docs/superpowers/specs/2026-09-23-site-health-design.md`.
 */
import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Play, Stethoscope } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Toggle } from "@/components/ui/toggle";
import { DisableDialog } from "@/pages/tina/site-health/components/disable-dialog";
import { FindingCard } from "@/pages/tina/site-health/components/finding-card";
import { PlatformCard } from "@/pages/tina/site-health/components/platform-card";
import { SnoozeDialog } from "@/pages/tina/site-health/components/snooze-dialog";
import {
  SITE_HEALTH_QUERY_ROOT,
  type SnoozeDays,
  useSiteHealthRepository,
} from "@/pages/tina/site-health/hooks/use-site-health-repository";
import { CHECK_LABEL } from "@/pages/tina/site-health/lib/check-labels";
import { isNewToday } from "@/pages/tina/site-health/lib/describe-finding";
import {
  type SiteHealthGroup,
  groupFindings,
  sortGroups,
} from "@/pages/tina/site-health/lib/group-findings";
import { DiscoverTestDialog } from "@/pages/tina/sites/components/discover-test-dialog";
import {
  SITE_HEALTH_CHECKS,
  type SiteHealthCheck,
  type SiteHealthFindingRow,
  type SiteHealthSeverity,
} from "@/types/tina";

const STALE_MS = 26 * 3_600_000;
/**
 * How long "Run now" stays off after a dispatched run. A forced run skips tina's
 * once-a-day guard, so every click would start another full run (a 90-day scan);
 * the job takes about a minute.
 */
const RUN_COOLDOWN_MS = 2 * 60_000;
const athensTime = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Athens",
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

type SeverityFilter = "all" | SiteHealthSeverity;

function targetKey(target: SiteHealthFindingRow[] | null): string {
  return target ? target.map((f) => f.id).join(",") : "none";
}

function Banner({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
      <span className="font-medium text-red-700 dark:text-red-300">{text}</span>
    </div>
  );
}

function Count({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

export const SiteHealthList = () => {
  const repo = useSiteHealthRepository();
  const [severity, setSeverity] = useState<SeverityFilter>("all");
  const [check, setCheck] = useState<SiteHealthCheck | "all">("all");
  const [newOnly, setNewOnly] = useState(false);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [search, setSearch] = useState("");
  const [snoozeTarget, setSnoozeTarget] = useState<SiteHealthFindingRow[] | null>(null);
  const [disableTarget, setDisableTarget] = useState<SiteHealthFindingRow[] | null>(null);
  const [testSite, setTestSite] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [coolingDown, setCoolingDown] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    },
    [],
  );
  const now = new Date();

  const openQ = useQuery({
    queryKey: [SITE_HEALTH_QUERY_ROOT, "open"],
    queryFn: () => repo.listFindings("open"),
  });
  // Fetched unconditionally, not gated on the toggle: the count strip needs the real
  // snoozed total even while the cards are hidden.
  const snoozedQ = useQuery({
    queryKey: [SITE_HEALTH_QUERY_ROOT, "snoozed"],
    queryFn: () => repo.listFindings("snoozed"),
  });

  const openRows = openQ.data?.items ?? [];
  const snoozedRows = snoozedQ.data?.items ?? [];
  const needle = search.trim().toLowerCase();
  function matchesFilters(r: SiteHealthFindingRow): boolean {
    return (
      (severity === "all" || r.severity === severity) &&
      (check === "all" || r.check_name === check) &&
      (!newOnly || isNewToday(r, now)) &&
      (!needle || r.site.toLowerCase().includes(needle))
    );
  }
  // Only OPEN findings are candidates for a platform card (spec §10.1: "2+ OPEN
  // findings"); a snoozed finding always renders as its own card, so it is never folded
  // into a group even when it shares platform+check with something else.
  const openGroups = sortGroups(groupFindings(openRows.filter(matchesFilters)), now);
  const snoozedGroups: SiteHealthGroup[] = sortGroups(
    snoozedRows.filter(matchesFilters).map((r) => ({
      key: r.id,
      platform: r.platform,
      checkName: r.check_name,
      severity: r.severity,
      findings: [r],
    })),
    now,
  );
  const groups = showSnoozed ? [...openGroups, ...snoozedGroups] : openGroups;

  const state = openQ.data?.state;
  const lastRun = state?.last_run_at ? new Date(state.last_run_at) : null;
  const hasRun = openQ.isSuccess && lastRun != null;
  const unclean = state?.last_run_status != null && state.last_run_status !== "ok";
  const stale = hasRun && lastRun != null && now.getTime() - lastRun.getTime() > STALE_MS;

  /**
   * Stops at the first failed or blocked call, and never toasts the "identity not
   * loaded" refusal more than once — `actorId` cannot change mid-batch, so the first
   * `repo.snooze`/`repo.disable` call already surfaces it and every later one would only
   * repeat it.
   */
  async function confirmSnooze(days: SnoozeDays, note: string | null): Promise<boolean> {
    for (const f of snoozeTarget ?? []) {
      if (!(await repo.snooze(f.id, days, note))) return false;
    }
    return true;
  }

  async function confirmDisable(reason: string): Promise<boolean> {
    for (const f of disableTarget ?? []) {
      if (!(await repo.disable(f.site, reason))) return false;
    }
    return true;
  }

  /**
   * One click, no dialog: closes every finding in `findings` (a single-finding card or a
   * whole platform group), stopping at the first failure like {@link confirmSnooze}. The
   * ref holds the finding ids in flight and is the source of truth for the guard —
   * checked and set synchronously, so two clicks landing before a re-render still cannot
   * both pass — while the paired state update only re-renders the buttons as disabled.
   * It is keyed per finding, not per clicked set: each solve refetches the list, and a
   * group rebuilt with fewer findings would no longer match a whole-set key.
   */
  const solvingRef = useRef<Set<string>>(new Set());
  const [, setSolvingTick] = useState(0);
  function isSolving(findings: SiteHealthFindingRow[]): boolean {
    return findings.some((f) => solvingRef.current.has(f.id));
  }
  async function onSolve(findings: SiteHealthFindingRow[]): Promise<void> {
    const todo = findings.filter((f) => !solvingRef.current.has(f.id));
    if (todo.length === 0) return;
    for (const f of todo) solvingRef.current.add(f.id);
    setSolvingTick((t) => t + 1);
    try {
      for (const f of todo) {
        if (!(await repo.solve(f.id))) return;
      }
    } finally {
      for (const f of todo) solvingRef.current.delete(f.id);
      setSolvingTick((t) => t + 1);
    }
  }

  async function onRunNow(): Promise<void> {
    if (running || coolingDown) return;
    setRunning(true);
    try {
      const outcome = await repo.runNow();
      if (outcome?.kind === "dispatched") {
        setCoolingDown(true);
        if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => setCoolingDown(false), RUN_COOLDOWN_MS);
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-2.5 py-0.5 text-[11px] font-medium">
            <Stethoscope className="h-3 w-3" />
            Tina · site health
          </div>
          <h1 className="mt-2 text-2xl font-bold tracking-tight">Site health</h1>
          <p className="text-sm text-muted-foreground">
            {`Checked at ${lastRun ? athensTime.format(lastRun) : "never"} (Athens). Sites that look broken, worst first.`}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void onRunNow()}
          disabled={running || coolingDown}
          title={coolingDown ? "A run was just started; wait for it before starting another." : undefined}
        >
          <Play className="mr-1.5 h-3.5 w-3.5" />
          {running ? "Running…" : coolingDown ? "Started" : "Run now"}
        </Button>
      </div>

      {openQ.isError ? (
        <Banner text="Could not load /site-health/findings." />
      ) : unclean ? (
        <Banner text="The last run did not finish cleanly — some checks are missing." />
      ) : !hasRun && openQ.isSuccess ? (
        <Banner text="No run yet — press Run now." />
      ) : (
        stale && <Banner text="No run in the last 26 hours." />
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Count label="Red" value={String(openRows.filter((r) => r.severity === "red").length)} />
        <Count label="Amber" value={String(openRows.filter((r) => r.severity === "amber").length)} />
        <Count label="New today" value={String(openRows.filter((r) => isNewToday(r, now)).length)} />
        <Count label="Snoozed" value={snoozedQ.isSuccess ? String(snoozedQ.data.total) : "—"} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={severity} onValueChange={(v) => setSeverity(v as SeverityFilter)}>
          <SelectTrigger className="h-8 w-[120px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All colours</SelectItem>
            <SelectItem value="red">Red</SelectItem>
            <SelectItem value="amber">Amber</SelectItem>
          </SelectContent>
        </Select>
        <Select value={check} onValueChange={(v) => setCheck(v as SiteHealthCheck | "all")}>
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All checks</SelectItem>
            {SITE_HEALTH_CHECKS.map((c) => (
              <SelectItem key={c} value={c}>
                {CHECK_LABEL[c]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Toggle size="sm" variant="outline" pressed={newOnly} onPressedChange={setNewOnly} className="h-8 px-2 text-xs">
          New today
        </Toggle>
        <Toggle size="sm" variant="outline" pressed={showSnoozed} onPressedChange={setShowSnoozed} className="h-8 px-2 text-xs">
          Show snoozed
        </Toggle>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by site…"
          className="h-8 w-[220px] text-xs"
        />
      </div>

      {openQ.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : openQ.isError ? null : groups.length === 0 ? (
        // Only claims "nothing is broken" once a run has actually happened — before that
        // an empty list means "never checked", which the "No run yet" banner above says.
        hasRun && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Nothing looks broken today.
          </div>
        )
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) =>
            g.findings.length > 1 ? (
              <PlatformCard
                key={g.key}
                group={g}
                now={now}
                onSnooze={setSnoozeTarget}
                onSolve={(findings) => void onSolve(findings)}
                isSolving={isSolving}
                onDisable={setDisableTarget}
                onDiscoverTest={setTestSite}
              />
            ) : (
              <FindingCard
                key={g.key}
                finding={g.findings[0]}
                now={now}
                onSnooze={() => setSnoozeTarget(g.findings)}
                onSolve={() => void onSolve(g.findings)}
                solveBusy={isSolving(g.findings)}
                onDisable={() => setDisableTarget(g.findings)}
                onDiscoverTest={() => setTestSite(g.findings[0].site)}
              />
            ),
          )}
        </div>
      )}

      {/* Keyed by the target, so a note or reason typed for one site never carries over to
          the next: a new target, or closing, remounts the dialog with empty fields. */}
      <SnoozeDialog
        key={`snooze:${targetKey(snoozeTarget)}`}
        open={snoozeTarget !== null}
        onOpenChange={(o) => !o && setSnoozeTarget(null)}
        title={snoozeTarget?.length === 1 ? snoozeTarget[0].site : `${snoozeTarget?.length ?? 0} sites`}
        onConfirm={confirmSnooze}
      />
      <DisableDialog
        key={`disable:${targetKey(disableTarget)}`}
        open={disableTarget !== null}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        sites={(disableTarget ?? []).map((f) => f.site)}
        onConfirm={confirmDisable}
      />
      <DiscoverTestDialog
        siteName={testSite}
        open={testSite !== null}
        onOpenChange={(o) => !o && setTestSite(null)}
      />
    </div>
  );
};
